import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import Button from "components/basic/button";
import Checkbox from "components/basic/checkbox";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useMaskedInput } from "hooks/useMaskedInput";
import { Vendor } from "pages/userAuthentication/signUp";
import { validateSignatory } from "pages/userAuthentication/signUp/helpers/signatoryValidation";
import { formatPhoneNumber, PHONE_NUMBER_PLACEHOLDER } from "pages/formTools";

const FIELDS = [
  { name: "signatoryFirstName", placeholder: "signatory.first_name" },
  { name: "signatoryLastName", placeholder: "signatory.last_name" },
  { name: "signatoryPosition", placeholder: "signatory.position" },
  { name: "signatoryEmail", placeholder: "signatory.email" },
  { name: "signatoryPhone", placeholder: null },
] as const;

type SignatoryDraft = Record<(typeof FIELDS)[number]["name"], string>;

const toDraft = (vendor: Vendor): SignatoryDraft => ({
  signatoryFirstName: vendor.signatoryFirstName ?? "",
  signatoryLastName: vendor.signatoryLastName ?? "",
  signatoryPosition: vendor.signatoryPosition ?? "",
  signatoryEmail: vendor.signatoryEmail ?? "",
  signatoryPhone: vendor.signatoryPhone ?? "",
});

interface SignatorySectionProps {
  vendor: Vendor;
  onSave: (
    updates: Partial<SignatoryDraft> & { primaryContactIsSignatory: boolean },
  ) => Promise<boolean>;
  /** True while the box is unticked and no signing authority is saved yet. */
  onIncompleteChange: (incomplete: boolean) => void;
}

/** Whether the primary contact can sign, else who the signing authority is. */
const SignatorySection: React.FC<SignatorySectionProps> = ({
  vendor,
  onSave,
  onIncompleteChange,
}) => {
  const theme = useTheme();
  const [canSign, setCanSign] = useState(vendor.primaryContactIsSignatory);
  const [draft, setDraft] = useState<SignatoryDraft>(() => toDraft(vendor));
  const [saving, setSaving] = useState(false);
  const { errors, validateAll, revalidate } = useFieldErrors(validateSignatory);
  const { t } = useTranslation("profile");
  const handlePhoneChange = useMaskedInput(formatPhoneNumber);

  // The saved state can only change from here, so a refetch just re-syncs it.
  useEffect(() => {
    setCanSign(vendor.primaryContactIsSignatory);
    setDraft(toDraft(vendor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.primaryContactIsSignatory, vendor.signatoryEmail]);

  useEffect(() => {
    onIncompleteChange(!canSign && vendor.primaryContactIsSignatory);
  }, [canSign, vendor.primaryContactIsSignatory, onIncompleteChange]);

  // Unticking only takes effect once a signing authority is saved below.
  const handleToggle = async (checked: boolean) => {
    setCanSign(checked);
    if (checked && !vendor.primaryContactIsSignatory) {
      setCanSign(await onSave({ primaryContactIsSignatory: true }));
    }
  };

  const handleSave = async () => {
    if (!validateAll(draft)) {
      return;
    }
    setSaving(true);
    await onSave({ primaryContactIsSignatory: false, ...draft });
    setSaving(false);
  };

  const update = (name: keyof SignatoryDraft, value: string) => {
    const next = { ...draft, [name]: value };
    setDraft(next);
    revalidate(next);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
      }}
    >
      <Checkbox
        isChecked={canSign}
        onChange={handleToggle}
        label={t("signatory.can_sign")}
      />

      {!canSign && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
            width: "100%",
          }}
        >
          <Typography variant="h5" align="center">
            {t("signatory.title")}
          </Typography>

          {FIELDS.map(({ name, placeholder }) => (
            <TextField
              key={name}
              name={name}
              placeHolder={
                placeholder ? t(placeholder) : PHONE_NUMBER_PLACEHOLDER
              }
              value={draft[name]}
              textAlign="center"
              onChange={(event) =>
                update(
                  name,
                  name === "signatoryPhone"
                    ? handlePhoneChange(event, draft.signatoryPhone)
                    : event.target.value,
                )
              }
              error={errors[name] ?? undefined}
            />
          ))}

          <Button
            variant="contained"
            color="warning"
            style={{ color: theme.palette.common.white, alignSelf: "center" }}
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            {t("signatory.save")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SignatorySection;
